import { module } from 'modujs';
import { createApp } from 'vue/dist/vue.esm-bundler.js'
import LisaStep from '../vue/components/LisaStep';
import LisaDialog from '../vue/components/LisaDialog'
import LisaForm from '../vue/components/LisaForm'
import LisaVisualizer from '../vue/components/LisaVisualizer'
import { reactive, watch } from 'vue'
import barba from "@barba/core";
import { lisa, LISA_BASE_PATH } from '../sixty/Lisa';
import gsap from 'gsap';
import vueFilePond from 'vue-filepond';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import { FocusTrap } from 'focus-trap-vue';
import pageVisibility from '../utils/visibility.js';

const FilePond = vueFilePond.default(FilePondPluginFileValidateType,FilePondPluginFileValidateSize);

export const lisaUserData = reactive({})

export default class extends module {
    constructor(m) {
        super(m);
    }

    start() {
        const that = this;

        pageVisibility.enableCustomEvents();

        const lisaContent = JSON.parse(that.getData('content'))
        that.el.removeAttribute('data-lisa-content');

        const assetsVersion = `${that.getData('assets-version')}`;
        that.el.removeAttribute('data-assets-version');

        const translations = JSON.parse(that.getData('translations'))
        that.el.removeAttribute('data-lisa-translations');

        const locale = that.getData('locale')
        that.el.removeAttribute('data-lisa-locale');

        this.app = createApp({
            delimiters: ['{(', ')}'],
            components: {
                LisaVisualizer,
                LisaStep: {
                    ...LisaStep,
                    components: {
                        FilePond,
                        LisaDialog,
                        LisaForm: {
                            ...LisaForm,
                            template: document.getElementById('lisa-template-form')
                        },
                        FocusTrap
                    },
                    template: document.getElementById('lisa-template-step')
                },
            },
            data() {
                return {
                    debugShow: false,
                    debugPersist: false,

                    showAudioDisclaimer: true,
                    forceCompact: false,

                    muted: false,
                    speaking: false,
                    stepIntroDone: false,

                    voiceSource: null,

                    lisaContent,
                    lisaUserData,
                    assetsVersion,

                    introNextStep: null,
                    forceSentence: null,

                    transitionName: 'default',

                    currentStepSlug: 'intro',
                    history: [],
                    errors: [],

                    locale
                }
            },
            mounted() {
                this.requestAudio();

                // Check URL for relevant parameters
                this.computeURLParams();

                this.bindEvents();

                this.documentTitleOriginal = document.title;

                this.stopWatchingGoal = watch(() => lisaUserData.goal, (newValue, oldValue) => {
                    if(newValue && translations['lisa.intro.choices.'+newValue]) {
                        const titleParts = document.title.split(' | ');
                        titleParts[0] = translations['lisa.intro.choices.'+newValue];
                        document.title = titleParts.join(' | ');
                    } else {
                        document.title = this.documentTitleOriginal;
                    }
                });
            },
            unmounted() {
                this.unbindEvents();

                this.stopWatchingGoal?.();
            },
            computed: {
                currentStep() {
                    return this.lisaContent[this.currentStepSlug]
                },
                canGoBack() {
                    const formFlowCompleted = !this.currentStepSlug.startsWith('culture-') && (this.currentStepSlug.includes('-completion') || this.currentStepSlug.includes('-processing'));

                    return this.history.length > 1 && !formFlowCompleted && this.currentStepSlug != 'form-error';
                },
                showStepContent() {
                    return ((!this.muted && !this.speaking) || this.muted) && this.stepIntroDone;
                },
                progress() {
                    return this.currentStep.progress || 0;
                },
                previousSentence() {
                    if(this.history.length < 2) return null;
                    return this.history[this.history.length-2].sentence;
                },
                isCompact() {
                    return this.showAudioDisclaimer || this.forceCompact || this.currentStep.isCompact;
                }
            },
            methods: {
                requestAudio() {
                    let mutedStored = localStorage.getItem('lisa-muted');
                    if(mutedStored == 'true') this.muted = true
                    if(document.visibilityState == 'hidden') return;

                    if(!this.muted && this.$refs['audio-ambient']) {
                        this.$refs['audio-ambient'].play().then(() => {
                            this.showAudioDisclaimer = false;
                        }).catch(error => {
                            console.error(error)
                            this.showAudioDisclaimer = true;
                        })
                    } else {
                        this.showAudioDisclaimer = false;
                    }
                },
                bindEvents() {
                    // Handle skip
                    that.el.addEventListener('click', this.skip)
                    window.addEventListener('resize', this.checkResize);
                    document.addEventListener('visibilityhidden', this.onVisibilityChange);
                    document.addEventListener('visibilityvisible', this.onVisibilityChange);
                },
                unbindEvents() {
                    that.el.removeEventListener('click', this.skip)
                    window.removeEventListener('resize', this.checkResize);
                    document.removeEventListener('visibilityhidden', this.onVisibilityChange);
                    document.removeEventListener('visibilityvisible', this.onVisibilityChange);
                },
                checkResize() {
                    if(!this.resizeTick) {
                        this.resizeTick = true
                        requestAnimationFrame(() => {
                            this.setXDecay(0);

                            this.resizeTick = false
                        })
                    }
                },
                setXDecay(duration = 1) {
                    if(this.showAudioDisclaimer) {
                        lisa.xDecay = 0;
                    } else {
                        const xDecay = window.innerWidth >= 1000 ? 1 : 0;
                        gsap.to(lisa, { xDecay, ease: 'Power4.out', duration });
                    }
                },
                computeURLParams() {
                    /*

                    Allowed URL Params:
                    data - Array of key/values following this pattern: `key1,value1;key2,value2`. Example: `goal,job;jobOffer,5cc35a48-c031-4b90-b41d-12829d90d204`
                    special - String for special dialogs, ex: `dynasty`
                    step - String, ID of the step to be displayed after `intro`

                    */

                    // Parse URL params
                    this.URLParams = new URL(document.location.toString()).searchParams;
                    const keys = Array.from(this.URLParams.keys());

                    if(keys.includes('data')) {
                        const data = this.URLParams.get('data').split(';').map(item => { let [key,value] = item.split(','); return {key,value} });

                        // Update the actual userData accordingly
                        for(let entry of data) {
                            const {key,value} = entry;
                            lisaUserData[key] = value;
                        }
                    }

                    if(keys.includes('step')) {
                        this.introNextStep = this.URLParams.get('step')
                    }

                    if(keys.includes('special')) {
                        this.lisaUserData['special'] = this.URLParams.get('special')
                    }
                },

                // ==========================================================================
                // NAVIGATION METHODS
                // ==========================================================================
                next() {
                    let next = this.currentStep.next;

                    // Handle next step
                    if(this.currentStepSlug == 'intro' && this.introNextStep && this.lisaContent[this.introNextStep]) {
                        this.history.push({ slug: 'greeting' })

                        next = this.introNextStep
                        this.introNextStep = null;

                        history.pushState("", document.title, window.location.pathname + window.location.search);
                    }

                    if(next && next.key) {
                        this.goToStep(next.values[lisaUserData[next.key]]);
                    } else if(next) {
                        this.goToStep(next);
                    }
                },
                nextPolite() {
                    if(!this.showStepContent) {
                        const unwatch = this.$watch('showStepContent', (value) => {
                            unwatch();
                            value && this.next();
                        })
                    } else this.next();
                },
                showError(errors) {
                    this.goToStep('form-error');
                    this.errors = errors;
                },
                showErrorPolite(errors) {
                    if(!this.showStepContent) {
                        const unwatch = this.$watch('showStepContent', (value) => {
                            unwatch();
                            value && this.showError(errors);
                        })
                    } else this.showError(errors);
                },
                formRetry() {
                    this.back(2);
                },
                back(amount = 1) {
                    if(this.history.length == 1) return;

                    if(typeof amount != 'number') amount = 1;

                    requestAnimationFrame(() => {
                        for(let i = 0; i < amount; i++) {
                            this.history.pop() // Remove current history
                        }
                        const prev = this.history.pop();
                        gtag?.('event', 'flow_step_back', {
                            flow: this.lisaUserData.goal,
                            origin: this.currentStepSlug,
                            target: prev.slug,
                        });
                        this.goToStep(prev.slug, prev.sentence, true);
                    })
                },
                goToStep(slug, forceSentence = null, backwards = false) {
                    if(!this.lisaContent[slug]) throw 'Step does not exist'

                    this.errors = [];
                    this.transitionName = backwards ? 'backwards' : 'default';
                    this.currentStepSlug = slug;
                    this.forceSentence = forceSentence;
                },
                historyPush(item) {
                    if(item.slug == 'greeting') {
                        // Clear history
                        const greetingsIndex = this.history.findIndex(item => item.slug == 'greeting');
                        if(greetingsIndex != -1)
                            this.history.splice(greetingsIndex+1)
                    }

                    this.history.push(item);
                },
                navigateTo(href) {
                    if(href.startsWith('mailto:') || href.startsWith('tel:'))
                        window.location.href = href;
                    else if(
                        !href.startsWith('http')
                        || window.location.origin == new URL(href).origin
                    )
                        barba.go(href);
                    else
                        window.open(href, '_blank')
                },

                // ==========================================================================
                // SENTENCE METHODS
                // ==========================================================================
                async playSpeech(src) {
                    this.$refs.visualizer?.audioCtx?.resume()

                    this.stopSpeech();

                    this.speaking = true;

                    // Retrieve audio as AudioBuffer
                    const res = await fetch(src);
                    const arrayBuf = await res.arrayBuffer();
                    const audioBuf = await this.$refs.visualizer?.audioCtx?.decodeAudioData(arrayBuf);

                    // Create a buffer source on the visualizer's audio context
                    this.voiceSource = this.$refs.visualizer?.audioCtx?.createBufferSource();
                    this.voiceSource.buffer = audioBuf;
                    this.voiceSource.connect(this.$refs.visualizer?.analyser);

                    // Start the audio playback
                    this.voiceSource.start();

                    // Set a timeout representing the end of the speech, as there's no event for it
                    this.voiceEndTimeout = setTimeout(() => {
                        this.sentenceEnded();
                    }, audioBuf.duration * 1000);
                },
                stopSpeech() {
                    if(this.voiceSource) {
                        this.voiceEndTimeout && clearTimeout(this.voiceEndTimeout);
                        this.voiceSource.disconnect();
                        this.voiceSource.stop();
                        this.voiceSource = null;
                        this.sentenceEnded();
                    }
                },
                skip(e) {
                    if(e.target == this.$refs.backbtn) {
                        if(this.speaking) {
                            this.stepIntroDone = false
                            this.stopSpeech();
                        }
                        return;
                    } else if(this.showAudioDisclaimer) {
                        this.acceptAudio();
                    } else if(this.speaking) {
                        this.stopSpeech();
                        this.$refs.step.skip();
                        this.stepIntroDone = true
                    }
                },
                sentenceEnded() {
                    // this.$refs.visualizer?.audioCtx?.suspend();
                    this.speaking = false;
                },
                updateStepIntro(value) {
                    this.stepIntroDone = value;
                },
                pickMedia(onComplete = undefined) {
                    // const videosToPick = [
                    //     // 'https://player.vimeo.com/progressive_redirect/playback/953679146/rendition/540p/file.mp4?loc=external&log_user=0&signature=6f6adb99c386852c650bf5842628b0e1c3e01f7bec4e96b95b345295322f3f5d',
                    //     // LISA_BASE_PATH+'idea.mp4',
                    //     // 'https://player.vimeo.com/progressive_redirect/playback/516052830/rendition/540p/file.mp4?loc=external&log_user=0&signature=c076080d9a3f8ff1c849f74f7b7d06189add04117657bdae836d7b346a4b39ca'
                    // ]

                    let mediaToPick;
                    let lisaSettings = { screenGlitchFrequency: 0.3, screenGlitchIntensity: 0.05, displayRunningCode: 0 };
                    if(this.currentStep && this.currentStep.media && this.currentStep.media.length) {
                        mediaToPick = this.currentStep.media
                    } else if(this.currentStep && this.currentStep.showForm) {
                        mediaToPick = [
                            ['https://stream.mux.com/mAXu5600Bhp70101nP4EU66jdEgBhSl4MIpBIbROPGCdis.m3u8', 'video'],
                        ]
                    } else if(Math.random() > 0.3) {
                        lisaSettings = { screenGlitchFrequency: 10, screenGlitchIntensity: 1, displayRunningCode: 1 };
                        // mediaToPick = [
                        //     ['https://stream.mux.com/HgALqBUzU6abuSbS401bOg02GUnAATTVzoh5Ae01hC8eK8.m3u8', 'video'],
                        //     ['https://stream.mux.com/6z8zie81zQDTSes5Y5CdgefmGU5KmiFGc01Okfjd9TtM.m3u8', 'video'],
                        //     ['https://stream.mux.com/CrWU4kQ7Y26KtHthUsB01TpSrkJibR3dWPg027r5awto00.m3u8', 'video'],
                        //     ['https://stream.mux.com/kOFddDAo8rxYoJ1Mx3q6G8BZa6YaGh4eSG4GsLMMk5s.m3u8', 'video'],
                        //     ['https://stream.mux.com/J5RN01RmzuwmdsPPGI0183zIkRG9e02eui9k4bj002Zh9aE.m3u8', 'video'],
                        //     // [LISA_BASE_PATH+'pattern-1.webm', 'video'],
                        //     // [LISA_BASE_PATH+'pattern-3.webm', 'video'],
                        //     // ['https://media4.giphy.com/media/v1.Y2lkPTc5MGI3NjExbHI2ZGIxbXhqc3U5NmJhajA2a3p4OHRrMmRkdDM3dHc2MXhybTZkbyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/l2QE3ZhG3iaPu8X9C/giphy.gif', 'gif'],
                        //     // ['https://media2.giphy.com/media/v1.Y2lkPTc5MGI3NjExaWUyNTA4b3h0bXNrdnJ3NmJ1Mng1Znhpd2Zwa2NlNHk3M2MzMDI2diZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/3oz8xS0liAv4XPOQTu/giphy.gif', 'gif'],
                        //     // ['https://media0.giphy.com/media/v1.Y2lkPTc5MGI3NjExYTViN2htMjRidDFtdHhnZmZpN2c3d3BiZmlyMjZjajBqamZ6OWhnOSZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/5th8zFFsvNOuM6nGsq/giphy.gif', 'gif'],
                        //     // ['https://media3.giphy.com/media/v1.Y2lkPTc5MGI3NjExZXp5aGZwc213cjdveTBybnBmejljOG1mMWh3azIycHRnc3lzcDRrcSZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/yDPDLJpd9lDFvszXHh/giphy.gif', 'gif'],
                        //     // ['https://media2.giphy.com/media/v1.Y2lkPTc5MGI3NjExYXlvZ3c5c296b3c0NTFrOHdhdmwwdXB4MHBuaGN4czBuMjg5bHVraSZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/xUOxfj6cTg3ezmjIoo/giphy.gif', 'gif'],
                        //     // ['https://media4.giphy.com/media/v1.Y2lkPTc5MGI3NjExcDJrcHBwYTYxZHpoNXVmMno2aGpwdzZqZzQ3MjZzNWhubXB5MDJkZSZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/RgzryV9nRCMHPVVXPV/giphy.gif', 'gif'],
                        //     // ['https://media0.giphy.com/media/v1.Y2lkPTc5MGI3NjExd3NiczVvN2NhZzcydmVveXJkeTFhYzJiYmwycmI4cXk4MXR4eWJkNyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/ewwd4xlxeSrM4aDDpL/giphy.gif', 'gif'],
                        //     // ['https://media4.giphy.com/media/v1.Y2lkPTc5MGI3NjExb2k2NjF5NjdkcHVyeGgwbjVuczN0NWQxN282dnA1MzAwOHZiYzJ4MCZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/uu6E9lMS091ao/giphy.gif', 'gif'],
                        //     // ['https://media3.giphy.com/media/v1.Y2lkPTc5MGI3NjExZTJzdzF5ZG1ncmJieTIycWhmOTUwc2JkajNza3NkaWRrcnZjNjN0bSZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/xT9IgtvfxOBxvqfWRW/giphy.gif', 'gif'],
                        //     // ['https://media0.giphy.com/media/v1.Y2lkPTc5MGI3NjExYjg0eDVnd3BuMWl3Mjlmd3c2Z3NibG5tNzI4ZmNyM2lueDRpbGt4byZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/l46CykJO7szNczcv6/giphy.gif', 'gif'],
                        //     // ['https://media1.giphy.com/media/v1.Y2lkPTc5MGI3NjExc2Vrejlpdzh3YXRrdDdyNnUzbHlobDUwYnd2cXRqamVkbW5mejJ6diZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/l4FGkxh96OxgRhPd6/giphy.gif', 'gif'],
                        //     // ['https://media2.giphy.com/media/v1.Y2lkPTc5MGI3NjExNjZwN3BkZDdnbTRvZDh6cG0wOTE1Y2s0djB4NTVycDhhcXY1eThsOCZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/3oriNNIUcKdx4SHpXa/giphy.gif', 'gif'],
                        //     // ['https://media3.giphy.com/media/v1.Y2lkPTc5MGI3NjExZmkxdzlocWl3eHBwbm02Ymoya2hoYzdtcDcycWdhenB6eWlsNnR4dSZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/fWgAV7ayPCSB0BUOnT/giphy.gif', 'gif'],
                        // ];
                    }

                    if(mediaToPick && mediaToPick.length) {
                        const media = mediaToPick[Math.floor(Math.random() * mediaToPick.length)];
                        lisa.setContent(media[0], media[1], onComplete);
                    }

                    lisa.idleTw?.kill?.();
                    gsap.to(lisa, lisaSettings)
                },
                setContent(content) {
                    clearTimeout(this.idleTimeout);
                    lisa.setContent(content[0], content[1]);
                },
                idleReset() {
                    lisa.setIdle();
                    if(this.currentStep.media) {
                        clearTimeout(this.idleTimeout);
                        this.idleTimeout = setTimeout(() => {
                            this.pickMedia(() => {
                                this.idleReset();
                            });
                        }, Math.random() * 15000 + 3000);
                    }
                },

                // ==========================================================================
                // GLOBAL AUDIO METHODS
                // ==========================================================================
                acceptAudio() {
                    requestAnimationFrame(() => {
                        this.$refs.visualizer?.audioCtx?.resume?.()
                        this.showAudioDisclaimer = false;
                        this.muted = false
                        this.$refs['audio-ambient'].play();
                        this.playSpeech();
                    })
                },
                stopAudio() {
                    this.stopSpeech();
                    this.$refs['audio-ambient'].pause();
                },
                resumeAudio() {
                    this.$refs['audio-ambient'].play();
                },
                muteToggle() {
                    this.muted = !this.muted;
                    this.$refs['audio-ambient'].muted = this.muted;

                    gtag?.('event', 'flow_audio_mute', {
                        flow: this.lisaUserData.goal,
                        step: this.currentStepSlug,
                        audio: this.muted ? 'muted' : 'unmuted'
                    });

                    if(this.muted) {
                        this.$refs['audio-ambient'].pause();
                        this.stopSpeech();
                    } else {
                        this.$refs['audio-ambient'].play();
                    }
                },
                onVisibilityChange(event) {
                    gtag?.('event', 'flow_visibility_change', {
                        flow: this.lisaUserData.goal,
                        step: this.currentStepSlug,
                        type: event.type,
                    });

                    if(event.type == 'visibilityhidden') {
                        this.stopAudio();
                    } else {
                        this.resumeAudio();
                    }
                }
            },
            watch: {
                showAudioDisclaimer: {
                    handler(value) {
                        if(value) {
                            lisa.setDisclaimer(this.locale);
                        } else {
                            lisa.setIdle();
                            this.pickMedia();
                        }
                        this.setXDecay()
                    },
                    immediate: true
                },
                isCompact: {
                    handler(value) {
                        that.el.classList.toggle('is-compact', value);
                        that.call('setCompact', value, 'LisaVisualizer');
                    },
                    immediate: true
                },
                muted() {
                    localStorage.setItem('lisa-muted', this.muted)
                },
                currentStepSlug(newValue, oldValue) {
                    this.stepIntroDone = false;

                    gtag('event', 'flow_step_view', {
                        flow: this.lisaUserData.goal,
                        step: newValue,
                        previous: oldValue
                    });

                    if(newValue.includes('-completion') && !newValue.includes('-pre-completion')) {
                        if(newValue.startsWith('culture-')) {
                            gtag?.('event', 'flow_culture_completion', {
                                flow: this.lisaUserData.goal,
                                step: newValue,
                                audio: this.muted ? 'muted' : 'unmuted'
                            });
                        } else {
                            gtag?.('event', 'flow_form_completion', {
                                flow: this.lisaUserData.goal,
                                audio: this.muted ? 'muted' : 'unmuted'
                            });
                        }
                    }

                    this.pickMedia();

                    setTimeout(() => {
                        document.documentElement.scrollTop = 0
                    }, 300);

                    // Clear userData & history if going back to greetings
                    if(this.currentStepSlug == 'greeting') {

                        if(lisaUserData.goal) {
                            gtag?.('event', 'flow_exit', {
                                flow: lisaUserData.goal,
                                step: newValue,
                                type: 'back',
                                audio: this.muted ? 'muted' : 'unmuted'
                            });
                        }

                        // Clear userData
                        for (const key in lisaUserData) {
                            delete lisaUserData[key];
                        }

                        lisaUserData.lang = locale;
                    }

                    if(this.debugPersist) {
                        let data = Object.entries(lisaUserData).map(entry => entry.join(','));
                        data = data.join(';');
                        const searchParams = new URLSearchParams({ step: this.currentStepSlug, data });
                        window.history.replaceState(null, '', window.location.origin+window.location.pathname+'?'+decodeURI(searchParams.toString()));
                    }

                    if(this.currentStepSlug == 'greeting' || this.currentStepSlug == 'confirmation') {
                        window.confirmPageChange = false;
                        window.onbeforeunload = null;
                        window.onunload = null;
                    } else {
                        requestAnimationFrame(() => {
                            window.confirmPageChange = {
                                context: 'lisa',
                                step: newValue,
                                audio: this.muted ? 'muted' : 'unmuted',
                                message: translations['lisa.confirm.quit'],
                            };
                        })

                        window.onbeforeunload = (e) => {
                            e.preventDefault();

                            gtag?.('event', 'flow_exit_attempt', {
                                flow: lisaUserData?.goal,
                                step: newValue,
                                type: 'beforeunload',
                                audio: this.muted ? 'muted' : 'unmuted'
                            });
                        }

                        window.onunload = () => {
                            gtag?.('event', 'flow_exit', {
                                flow: lisaUserData?.goal,
                                step: newValue,
                                type: 'unload',
                                audio: this.muted ? 'muted' : 'unmuted'
                            });
                        }
                    }
                },
                showStepContent(value) {
                    if(this.muted) this.sentenceEnded();

                    if(value) {
                        if(this.currentStep.next && !this.currentStep.inputs && !this.currentStep.choices && !this.currentStep.showForm) {
                            this.next()
                        }

                        //// Set 3D monitor content
                        lisa.setIdle();
                        // Show currentStep media periodically after idle
                        this.idleReset();
                    } else {
                        clearTimeout(this.idleTimeout);
                    }
                }
            }
        })
        this.app.mount(this.el);
    }

    destroy() {
        window.confirmPageChange = false;
        window.onbeforeunload = null;
        window.onunload = null;

        if(this.idleTimeout) {
            clearTimeout(this.idleTimeout);
        }

        pageVisibility.disableCustomEvents();

        this.app && this.app.unmount();
    }
}
